<!-- src/modules/okr/views/OkrDashboard.vue -->
<template>
  <b-container fluid class="okr-dashboard p-0 pt-1">
    <b-row class="mb-1">
      <b-col>
        <div class="nav-bar">
          <b-nav pills>
            <b-nav-item
              v-if="scopeOptions.includes(structureTypes.OWN)"
              @click="selectScope(structureTypes.OWN)"
              :active="selectedScope === structureTypes.OWN && viewMode !== 'reports'"
              class="profile-nav mr-1 mb-1"
              :disabled="loadingData"
            >
              Meu OKR
            </b-nav-item>

            <b-nav-item
              v-if="scopeOptions.includes(structureTypes.DIRECT)"
              @click="selectScope(structureTypes.DIRECT)"
              :active="selectedScope === structureTypes.DIRECT && viewMode !== 'reports'"
              class="profile-nav mr-1 mb-1"
              :disabled="loadingData"
            >
              Equipe direta
            </b-nav-item>

            <!-- temporário: em manutenção -->
            <b-nav-item
              v-if="scopeOptions.includes(structureTypes.STRUCTURE)"
              class="profile-nav mr-1 mb-1 nav-maintenance"
              disabled
              @click.prevent
            >
              Estrutura
              <b-badge variant="light-secondary" class="ml-50 font-small-1">Em manutenção</b-badge>
            </b-nav-item>

            <!-- temporário: em manutenção -->
            <b-nav-item
              class="profile-nav mb-1 nav-maintenance"
              disabled
              @click.prevent
            >
              Histórico trimestral
              <b-badge variant="light-secondary" class="ml-50 font-small-1">Em manutenção</b-badge>
            </b-nav-item>
          </b-nav>
        </div>
      </b-col>
    </b-row>
    <b-row class="mb-1 mr-2">
      <b-col cols="12" md="8" class="d-flex align-items-center mb-1 mb-md-0">
        <h2 class="mb-0">{{ pageTitle }}</h2>
      </b-col>
      <b-col
        cols="12"
        md="4"
        class="view-mode d-flex align-items-center justify-content-end mb-1 mb-md-0"
        v-if="selectedScope !== structureTypes.OWN && (viewMode == 'dashboard' || viewMode == 'pn-view')"
      >
        <b-button
          :variant="viewMode === 'dashboard' ? 'success' : 'outline-success'"
          @click="viewMode = 'dashboard'"
          class="mr-1"
        >
          Dashboard
        </b-button>
        <b-button
          :variant="viewMode === 'pn-view' ? 'info' : 'outline-info'"
          @click="viewMode = 'pn-view'"
        >
          Visão dos PNs
        </b-button>
      </b-col>
    </b-row>

    <OkrGanhosBlock
      v-if="viewMode === 'dashboard'"
      :year="selectedYear"
      :quarter="selectedQuarter"
      :structure-type="selectedScope"
      :consultant-id="consultantId"
    >
      <b-col cols="12" md="6" :lg="selectedScope === structureTypes.OWN ? 3 : 4">
        <b-card no-body class="border-0 shadow">
          <b-card-header class="d-flex justify-content-between align-items-center">
            <h4 class="mb-0">Ações e Filtros</h4>
          </b-card-header>
          <b-card-body>
            <p class="text-muted">
              Registre seus OKR ou veja os OKR de trimestres anteriores.
            </p>
            <b-row>
              <!-- Em duas linhas: botão em cima, trimestre/ano embaixo -->
              <b-col cols="12" class="mb-1">
                <b-button
                  v-if="!isQuarterClosed"
                  :variant="hasFilledOkr ? 'warning' : 'primary'"
                  block
                  @click="openOkrForm"
                >
                  {{ hasFilledOkr ? "Editar OKR" : "Registrar OKR" }}
                  {{
                    selectedScope === structureTypes.DIRECT ? "- Equipe direta" :
                    selectedScope === structureTypes.STRUCTURE ? " - Estrutura" : ""
                  }}
                </b-button>
                <div v-else class="quarter-closed-hint">
                  <feather-icon icon="LockIcon" size="14" class="mr-25" />
                  Trimestre encerrado — somente consulta
                </div>
              </b-col>
              <b-col cols="12">
                <label class="d-flex align-items-center mb-25 small text-muted">
                  Trimestre/Ano
                  <feather-icon
                    id="quarter-info-dash"
                    icon="InfoIcon"
                    size="12"
                    class="ml-1 cursor-pointer"
                  />
                  <b-tooltip target="quarter-info-dash" triggers="hover" placement="top">
                    T1: Jan – Mar &nbsp;·&nbsp; T2: Abr – Jun &nbsp;·&nbsp; T3: Jul – Set &nbsp;·&nbsp; T4: Out – Dez
                  </b-tooltip>
                </label>
                <div class="d-flex">
                  <v-select
                    v-model="selectedQuarter"
                    :options="quarterOptions"
                    :clearable="false"
                    :reduce="opt => opt.value"
                    label="label"
                    class="mr-1"
                    style="min-width: 65px; flex: 1"
                    @input="loadData"
                  />
                  <v-select
                    v-model="selectedYear"
                    :options="yearOptions"
                    :clearable="false"
                    style="min-width: 75px; flex: 1"
                    @input="loadData"
                  />
                </div>
                <small class="text-muted">{{ quarterMonthsLabel }}</small>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>
    </OkrGanhosBlock>

    <div v-if="viewMode === 'pn-form' && selectedScope === structureTypes.OWN">
      <b-row>
        <b-col cols="12">
          <OkrFormNew
            :consultant-id="consultantId"
            :year="selectedYear"
            :quarter="selectedQuarter"
            @saved="onOkrSaved"
            @close="viewMode = 'dashboard'"
          />
        </b-col>
      </b-row>
    </div>

    <!-- DIRECT/STRUCTURE scope: team list form -->
    <okr-consultant-list
      v-if="viewMode === 'pn-form' && selectedScope !== structureTypes.OWN"
      :year="selectedYear"
      :quarter="selectedQuarter"
      :structure-type="selectedScope"
      @close="viewMode = 'dashboard'"
    />

    <b-row v-if="viewMode === 'dashboard'">
      <b-col cols="12">
        <b-card no-body class="border-0 shadow">
          <b-card-header
            class="d-flex justify-content-between align-items-center cursor-pointer"
            @click="showFunnel = !showFunnel"
          >
            <h4 class="mb-0">Funil de OKR — Planejado × Realizado</h4>
            <feather-icon
              :icon="showFunnel ? 'ChevronUpIcon' : 'ChevronDownIcon'"
              size="18"
              class="text-muted"
            />
          </b-card-header>
          <b-collapse v-model="showFunnel">
            <b-card-body>
              <div v-if="loadingTaskTable" class="text-center py-5">
                <b-spinner variant="primary" />
              </div>
              <b-row v-else>
                <b-col cols="12" md="6" class="funnel-col">
                  <OkrFunnelChart
                    :planned="funnelPlanned"
                    :realized="funnelRealized"
                    :scale-max="funnelScaleMax"
                    mode="planned"
                  />
                </b-col>
                <b-col cols="12" md="6" class="funnel-col">
                  <OkrFunnelChart
                    :planned="funnelPlanned"
                    :realized="funnelRealized"
                    :scale-max="funnelScaleMax"
                    mode="realized"
                  />
                </b-col>
              </b-row>
            </b-card-body>
          </b-collapse>
        </b-card>
      </b-col>
    </b-row>

    <b-row v-if="viewMode === 'dashboard'">
      <b-col cols="12">
        <TaskTrackerTable
          v-if="!loadingTaskTable"
          :title="'Acompanhamento de suas tarefas'"
          :tasks="taskList"
        />
        <b-card v-else no-body class="border-0 shadow">
          <b-card-body>
            <div class="text-center py-5">
              <b-spinner variant="primary" />
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <template v-if="viewMode === 'reports'">
      <OkrReports />
    </template>

    <!-- No-OKR registration prompt (own scope, no planned OKR yet) -->
    <b-alert
      v-if="viewMode === 'dashboard' && selectedScope === structureTypes.OWN && !myOkr.exists && !isQuarterClosed"
      show
      variant="warning"
      class="mt-1 d-flex align-items-center justify-content-between"
    >
      <span>
        <feather-icon icon="AlertCircleIcon" size="16" class="mr-50" />
        Você ainda não registrou seu OKR para este trimestre. Os dados de acompanhamento só aparecem após o registro.
      </span>
      <b-button size="sm" variant="warning" @click="openOkrForm">Registrar OKR</b-button>
    </b-alert>

    <b-row v-if="viewMode === 'dashboard' && selectedScope === structureTypes.OWN" class="mt-1">
      <b-col cols="12">
        <OkrWeeklyTracking
          :consultant-id="consultantId"
          :year="selectedYear"
          :quarter="selectedQuarter"
          :read-only="isQuarterClosed"
        />
      </b-col>
    </b-row>

    <OkrConsultantsOverview
      v-if="viewMode === 'pn-view'"
      :structure-type="selectedScope"
      :year="selectedYear"
      :quarter="selectedQuarter"
    />
  </b-container>
</template>

<script>
import {
  BContainer,
  BRow,
  BCol,
  BButton,
  BAlert,
  BCard,
  BCardBody,
  BCardHeader,
  BFormGroup,
  BFormSelect,
  BProgress,
  BSpinner,
  BModal,
  BOverlay,
  BNav,
  BNavItem,
  BTooltip,
  BBadge,
  BCollapse,
} from "bootstrap-vue";
import FeatherIcon from "@core/components/feather-icon/FeatherIcon.vue";
import vSelect from "vue-select";
import useVuelidate from "@vuelidate/core";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import TaskTrackerTable from "../components/TaskTrackerTable.vue";
import OkrReports from "../components/OkrReports.vue";
import OkrConsultantsOverview from "./OkrConsultantsOverview.vue";
import OkrGanhosBlock from "../components/OkrGanhosBlock.vue";
import OkrFormNew from "../components/OkrFormNew.vue";
import OkrFunnelChart from "../components/OkrFunnelChart.vue";
import OkrWeeklyTracking from "../components/OkrWeeklyTracking.vue";
import { mapActions } from "vuex";
import * as types from "@/modules/okr/store/types";
import { getCredentials } from "@/helpers/credentials";
import { ADMIN_CREDENTIALS_STORAGE_KEY } from "@/constants/auth";
import OkrConsultantList from "./OkrConsultantList.vue";
import { processTaskList } from "@/helpers/okr";
import * as structureTypes from "@/constants/structure_options";

const QUARTER_MONTHS = {
  1: "Jan – Mar",
  2: "Abr – Jun",
  3: "Jul – Set",
  4: "Out – Dez",
};

export default {
  name: "OkrDashboard",
  components: {
    BContainer,
    BRow,
    BCol,
    BButton,
    BAlert,
    FeatherIcon,
    BCard,
    BCardBody,
    BCardHeader,
    BFormGroup,
    BFormSelect,
    BProgress,
    BSpinner,
    BModal,
    BOverlay,
    BTooltip,
    vSelect,
    OkrConsultantList,
    TaskTrackerTable,
    OkrReports,
    OkrConsultantsOverview,
    OkrGanhosBlock,
    OkrFormNew,
    OkrFunnelChart,
    OkrWeeklyTracking,
    BNav,
    BNavItem,
    BBadge,
    BCollapse,
  },
  setup() {
    return {
      v$: useVuelidate(),
      toast: useToast(),
    };
  },
  data() {
    return {
      structureTypes,
      scopeOptions: [],
      selectedScope: structureTypes.OWN,
      viewMode: "dashboard",
      selectedYear: new Date().getFullYear(),
      selectedQuarter: Math.ceil((new Date().getMonth() + 1) / 3),
      minYear: 2022,
      maxYear: new Date().getFullYear() + 1,
      loading: false,
      loadingChart: false,
      loadingTaskTable: false,
      loadingData: false,
      showFunnel: false,
      consultantId: null,
      consultantName: "",
      myOkr: {
        current: 0,
        target: 2500,
        percentage: 0,
        exists: false,
      },
      consultantOkrForm: false,
      currentPerformanceData: null,
      hasFilledOkr: false,
      quarterOptions: [
        { value: 1, label: "T1" },
        { value: 2, label: "T2" },
        { value: 3, label: "T3" },
        { value: 4, label: "T4" },
      ],
    };
  },
  computed: {
    yearOptions() {
      const years = [];
      for (let y = this.minYear; y <= this.maxYear; y++) years.push(y);
      return years;
    },
    quarterMonthsLabel() {
      return QUARTER_MONTHS[this.selectedQuarter] || "";
    },
    isQuarterClosed() {
      const now = new Date();
      const currentQuarter = Math.ceil((now.getMonth() + 1) / 3);
      const year = Number(this.selectedYear);
      const quarter = Number(this.selectedQuarter);
      return (
        year < now.getFullYear() ||
        (year === now.getFullYear() && quarter < currentQuarter)
      );
    },
    pageTitle() {
      if (this.viewMode === "reports") return "Histórico trimestral";
      const editLabel = this.viewMode == "pn-form" ? "Editar " : "";
      return {
        own: editLabel + "Meu OKR",
        direct: editLabel + "OKR - Equipe direta",
        structure: editLabel + "OKR - Estrutura",
      }[this.selectedScope];
    },
    taskList() {
      return processTaskList(this.currentPerformanceData);
    },
    // funil e tabela usam a mesma resposta para os números baterem
    funnelPlanned() {
      return this.mapFunnelData(this.currentPerformanceData?.planned_okr);
    },
    funnelRealized() {
      return this.mapFunnelData(this.currentPerformanceData?.realized_okr);
    },
    funnelScaleMax() {
      const values = [
        ...Object.values(this.funnelPlanned || {}),
        ...Object.values(this.funnelRealized || {}),
      ].map(Number);
      return Math.max(...values, 1);
    },
  },
  async created() {
    const creds = getCredentials(ADMIN_CREDENTIALS_STORAGE_KEY);
    this.consultantId = creds?.user?.appify_consultant_id;
    this.consultantName = creds?.user?.name || "Consultor";

    try {
      this.scopeOptions = await this.getScopeOptions();
      if (!this.scopeOptions.includes("reports")) {
        this.scopeOptions.push("reports");
      }
      if (!this.scopeOptions.includes(this.selectedScope)) {
        this.selectedScope = this.scopeOptions[0] || "own";
      }
      // temporário: Estrutura em manutenção
      if (this.selectedScope === structureTypes.STRUCTURE) {
        this.selectedScope =
          this.scopeOptions.find(
            (s) => s === structureTypes.OWN || s === structureTypes.DIRECT
          ) || structureTypes.OWN;
      }

      const quarterInterval = await this.getQuartersInterval();
      if (quarterInterval && quarterInterval.initial_year) {
        this.minYear = quarterInterval.initial_year;
        this.maxYear = quarterInterval.end_year;
      }

      const { year, quarter } = this.getDefaultQuarter();
      this.selectedYear = year;
      this.selectedQuarter = quarter;

      this.loadData();
    } catch (error) {
      this.toast({
        component: ToastificationContent,
        props: {
          title: "Erro",
          text: "Falha ao carregar dados iniciais.",
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
    }
  },
  methods: {
    ...mapActions({
      getPlannedOkr: types.GET_PLANNED_OKR,
      getScopeOptions: types.GET_SCOPE_OPTIONS,
      getQuartersInterval: types.GET_MONTHS_INTERVAL,
      getCurrentPerformanceTableData: types.GET_CURRENT_PERFORMANCE_TABLE_DATA,
      getHasFilledOkr: types.GET_HAS_FILLED_OKR,
    }),
    getDefaultQuarter() {
      const now = new Date();
      return {
        year: now.getFullYear(),
        quarter: Math.ceil((now.getMonth() + 1) / 3),
      };
    },
    async selectScope(scope) {
      if (this.scopeOptions.includes(scope)) {
        this.selectedScope = scope;
        if (this.selectedScope === structureTypes.OWN) {
          this.viewMode = "dashboard";
        } else if (this.viewMode === "pn-form") {
          this.viewMode = "dashboard";
        } else if (this.viewMode === "reports") {
          this.viewMode = "dashboard";
        }
        if (this.viewMode == "dashboard") {
          await this.loadData();
        }
      }
    },
    mapFunnelData(source) {
      if (!source) return null;
      return {
        leads_quantity:     source.lead_quantity     ?? 0,
        hot_leads_quantity: source.hot_lead_quantity ?? 0,
        ea_quantity:        source.ea_quantity        ?? 0,
        ef_quantity:        source.ef_quantity        ?? 0,
        ep_quantity:        source.ep_quantity        ?? 0,
      };
    },
    async loadData() {
      this.loadingData = true;
      const tasks = [
        this.loadFilledOkrStatus(),
        this.loadCurrentPerformanceData(),
      ];
      if (this.selectedScope === structureTypes.OWN) {
        tasks.push(this.loadMyOkr());
      }
      await Promise.all(tasks);
      this.loadingData = false;
    },
    async loadMyOkr() {
      this.loading = true;
      const payload = {
        year: this.selectedYear,
        quarter: this.selectedQuarter,
        consultant_id: this.consultantId,
      };
      return this.getPlannedOkr(payload)
        .then((response) => {
          const data = response.data;
          if (data && Object.keys(data).length != 0) {
            this.myOkr.current = data.okr_total || 0;
            this.myOkr.exists = true;
            this.myOkr.percentage =
              this.myOkr.current && this.myOkr.target
                ? Math.round((this.myOkr.current / this.myOkr.target) * 100)
                : 0;
          } else {
            this.myOkr.exists = false;
            this.myOkr.current = 0;
            this.myOkr.percentage = 0;
          }
        })
        .catch((error) => {
          this.myOkr.exists = false;
          this.myOkr.current = 0;
          this.myOkr.percentage = 0;
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: error?.response?.data?.message || "Ocorreu um erro ao carregar seus OKR.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    async loadCurrentPerformanceData() {
      this.loadingTaskTable = true;
      try {
        const payload = {
          year: this.selectedYear,
          quarter: this.selectedQuarter,
          structure_type: this.selectedScope,
        };
        const response = await this.getCurrentPerformanceTableData(payload);
        this.currentPerformanceData = response?.data || null;
      } catch (error) {
        this.currentPerformanceData = null;
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: error?.response?.data?.message || "Ocorreu um erro ao carregar os dados de performance atual.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.loadingTaskTable = false;
      }
    },

    async loadFilledOkrStatus() {
      this.hasFilledOkr = false;
      try {
        const payload = {
          year: this.selectedYear,
          quarter: this.selectedQuarter,
          structure_type: this.selectedScope,
        };
        const response = await this.getHasFilledOkr(payload);
        this.hasFilledOkr = response?.data?.has_filled_okr || false;
      } catch (error) {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: error?.response?.data?.message || "Ocorreu um erro ao verificar o status do OKR.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      }
    },

    openOkrForm() {
      if (this.isQuarterClosed) return;
      this.viewMode = "pn-form";
    },

    async onOkrSaved() {
      this.viewMode = "dashboard";
      await this.loadData();
    },
  },
};
</script>

<style lang="scss" scoped>
.page-title {
  font-weight: 500;
  font-size: 1.5rem;
}

.card-header {
  padding: 1.5rem 1.5rem 1rem 1.5rem !important;
}

.scope-menu .active,
.scope-menu .btn-primary {
  z-index: 1;
}

.scope-menu .btn {
  width: fit-content !important;
  margin-right: 1rem;
}

.view-mode .btn {
  font-size: 0.875rem;
}

.cursor-pointer {
  cursor: pointer;
}

/* limita a largura dos funis */
.funnel-col ::v-deep .okr-funnel-chart {
  max-width: 420px;
  margin: 0 auto;
}

.quarter-closed-hint {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  font-weight: 600;
  color: #6e6b7b;
  background: rgba(110, 107, 123, 0.08);
  border-radius: 6px;
  padding: 0.55rem 0.75rem;
}

/* visões em manutenção */
.nav-maintenance {
  opacity: 0.65;
}
.nav-maintenance .nav-link {
  cursor: not-allowed !important;
}
</style>
